/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useCallback } from 'react';

import { jsx, cssMap as unboundedCssMap } from '@compiled/react';

import { cssMap } from '@atlaskit/css';
import { Box, Flex, Pressable } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { InsightLinkCardGroup } from '@atlassian/rovo-platform-ui-components/insight-link-card-group';

import { ProfileAvatarGroup, type ProfilePerson } from '../../../../common/ui/profile-avatar-group';

export interface TrendingContentCellItem {
	id: string;
	title: string;
	description: string;
	/** Optional bold prefix for the description — e.g. the entity name that precedes the summary text */
	descriptionBoldPrefix?: string;
	/** Large numeric metric value — e.g. "78" */
	metricValue: string | number;
	/** Unit label displayed beside the metric — e.g. "views" */
	metricUnit: string;
	/** Optional list of people whose profile avatars should appear on the card. */
	people?: ProfilePerson[];
	/** URLs to render as InsightLinkCard components below the cell content. */
	urls?: string[] | null;
}

const styles = cssMap({
	// Background is transparent so the outer wrapper controls the hover/active
	// highlight for the full card area (including the link-cards section).
	cell: {
		width: '100%',
		display: 'flex',
		flexDirection: 'column',
		paddingTop: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
		paddingRight: token('space.200'),
		cursor: 'pointer',
		gap: token('space.100'),
		borderRadius: token('radius.xlarge'),
		backgroundColor: 'transparent',
	},
	cellContent: {
		width: '100%',
	},
	metricRow: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		flexWrap: 'nowrap',
	},
	metricValueWrapper: {
		display: 'flex',
		alignItems: 'baseline',
		gap: token('space.050'),
		paddingRight: token('space.100'),
		flexGrow: 1,
		minWidth: '0px',
		minHeight: '40px',
	},
	avatarChevronGroup: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.100'),
		flexShrink: 0,
	},
	descriptionSection: {
		display: 'flex',
		flexDirection: 'column',
		textAlign: 'left',
	},
});

// Vendor-prefixed and unconstrained styles — use @compiled/react cssMap directly
const vendorStyles = unboundedCssMap({
	metricValue: {
		font: token('font.heading.xxlarge'),
		color: token('color.text.success'),
		margin: 0,
	},
	metricUnit: {
		font: token('font.heading.xsmall'),
		color: token('color.text'),
		margin: 0,
	},
	descriptionText: {
		display: '-webkit-box',
		margin: 0,
		overflow: 'hidden',
		WebkitLineClamp: 2,
		WebkitBoxOrient: 'vertical',
		textOverflow: 'ellipsis',
		font: token('font.body.small'),
		color: token('color.text.subtlest'),
	},
	descriptionBold: {
		font: token('font.body.small'),
		fontWeight: token('font.weight.medium'),
		color: token('color.text.subtlest'),
	},
	// Outer wrapper that carries the bottom border delimiter instead of the Pressable,
	// so that link cards appear inside the bordered section before the delimiter.
	// Bottom delimiter rendered as a separate full-width div inside the overflow:hidden
	// wrapper, so it is not clipped. Placed after link cards as the last element.
	delimiter: {
		borderBottomWidth: token('border.width'),
		borderBottomStyle: 'solid',
		borderBottomColor: token('color.border'),
	},
	// Stretches the content Pressable's hit area over the whole cell (anchored to the
	// position:relative wrapper) so any click activates that one button.
	clickOverlay: {
		position: 'absolute',
		insetBlockStart: token('space.0', '0px'),
		insetInlineStart: token('space.0', '0px'),
		insetBlockEnd: token('space.0', '0px'),
		insetInlineEnd: token('space.0', '0px'),
	},
	// pointer-events:none lets padding clicks fall through to the overlay; linkInteractive
	// re-enables them on the link cards so those stay independently clickable.
	linkGroupWrapper: {
		position: 'relative',
		pointerEvents: 'none',
		paddingLeft: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.150'),
	},
	linkInteractive: {
		pointerEvents: 'auto',
	},
});

/** Controls border-radius and border per cell position within a group list. */
export type TrendingContentCellPosition = 'only' | 'first' | 'middle' | 'last';

// Position-dependent wrapper styles for clipping within a grouped container.
// Hover/active/cursor styles live here so the entire card area — including the
// link-cards section below the Pressable — highlights on mouse hover.
const positionStyles = unboundedCssMap({
	only: {
		borderTopLeftRadius: token('radius.small'),
		borderTopRightRadius: token('radius.small'),
		borderBottomLeftRadius: token('radius.small'),
		borderBottomRightRadius: token('radius.small'),
		overflow: 'hidden',
		cursor: 'pointer',
		position: 'relative',
		backgroundColor: token('elevation.surface.raised'),
		'&:hover': {
			backgroundColor: token('elevation.surface.hovered'),
		},
		'&:active': {
			backgroundColor: token('elevation.surface.pressed'),
		},
	},
	first: {
		borderTopLeftRadius: token('radius.small'),
		borderTopRightRadius: token('radius.small'),
		overflow: 'hidden',
		cursor: 'pointer',
		position: 'relative',
		backgroundColor: token('elevation.surface.raised'),
		'&:hover': {
			backgroundColor: token('elevation.surface.hovered'),
		},
		'&:active': {
			backgroundColor: token('elevation.surface.pressed'),
		},
	},
	middle: {
		overflow: 'hidden',
		cursor: 'pointer',
		position: 'relative',
		backgroundColor: token('elevation.surface.raised'),
		'&:hover': {
			backgroundColor: token('elevation.surface.hovered'),
		},
		'&:active': {
			backgroundColor: token('elevation.surface.pressed'),
		},
	},
	last: {
		borderBottomLeftRadius: token('radius.small'),
		borderBottomRightRadius: token('radius.small'),
		overflow: 'hidden',
		cursor: 'pointer',
		position: 'relative',
		backgroundColor: token('elevation.surface.raised'),
		'&:hover': {
			backgroundColor: token('elevation.surface.hovered'),
		},
		'&:active': {
			backgroundColor: token('elevation.surface.pressed'),
		},
	},
});

export interface TrendingContentCellProps {
	/** The insight cell data to render */
	item: TrendingContentCellItem;
	/** Called when the cell is pressed, with the insight's id */
	onInsightPress: (insightId: string) => void;
	/**
	 * Position of this cell within the group list — controls border-radius and which
	 * borders are shown to form a seamless grouped visual.
	 * Defaults to 'only' (all corners rounded, full border).
	 */
	position?: TrendingContentCellPosition;
	/** Whether the cell is rendered in fullscreen layout. */
	isFullScreen?: boolean;
	/** Optional callback invoked when a link card within the cell is clicked. */
	onLinkClick?: (url: string) => void;
}

/**
 * TrendingContentCell — A metric-focused cell layout for trending content insights.
 *
 * Displays a prominent metric value (e.g. "78 views") alongside an avatar group,
 * a descriptive summary, and optional URL link cards at the bottom.
 * Designed for insights that highlight quantitative data such as page views,
 * engagement counts, or activity metrics.
 */
export const TrendingContentCell = ({
	item,
	onInsightPress,
	position = 'only',
	isFullScreen = false,
	onLinkClick,
}: TrendingContentCellProps): React.JSX.Element => {
	const handleClick = useCallback(() => {
		onInsightPress(item.id);
	}, [onInsightPress, item.id]);

	const hasDelimiter = position === 'first' || position === 'middle';

	const urlList = item.urls && item.urls.length > 0 ? item.urls : null;

	return (
		<div css={positionStyles[position]}>
			<Pressable xcss={styles.cell} onClick={handleClick}>
				<span aria-hidden="true" css={vendorStyles.clickOverlay} />
				<Flex
					direction="column"
					gap={isFullScreen ? 'space.150' : 'space.100'}
					xcss={styles.cellContent}
				>
					{/* Metric row: large value + unit, avatar group + chevron */}
					<Flex xcss={styles.metricRow}>
						<Flex xcss={styles.metricValueWrapper}>
							<span css={vendorStyles.metricValue}>{item.metricValue}</span>
							<span css={vendorStyles.metricUnit}>{item.metricUnit}</span>
						</Flex>
						<Flex xcss={styles.avatarChevronGroup}>
							{item.people && item.people.length > 0 && (
								<ProfileAvatarGroup people={item.people} testId="insight-cell-avatars" />
							)}
						</Flex>
					</Flex>

					{/* Description */}
					<Box xcss={styles.descriptionSection}>
						<span css={vendorStyles.descriptionText}>
							{item.descriptionBoldPrefix && (
								<span css={vendorStyles.descriptionBold}>{item.descriptionBoldPrefix}</span>
							)}
							{item.descriptionBoldPrefix ? ' ' : ''}
							{item.description}
						</span>
					</Box>
				</Flex>
			</Pressable>
			{urlList && (
				<div css={vendorStyles.linkGroupWrapper}>
					<div css={vendorStyles.linkInteractive}>
						<InsightLinkCardGroup
							urls={urlList}
							isFullScreen={isFullScreen}
							onLinkClick={onLinkClick}
						/>
					</div>
				</div>
			)}
			{hasDelimiter && <div css={vendorStyles.delimiter} />}
		</div>
	);
};
